import React, { useState } from 'react';
import { 
  FinancialRecordInput, 
  FinancialRecord, 
  KPI, 
  KPICategory,
  KPIEnrichment 
} from '../../types/data/financialRecord';
import {
  enrichKPI,
  parseKPIValue,
  groupFinancialInputs,
  convertInputsToRecords,
  validateFinancialInputs,
  getKPIStatistics,
  STANDARD_KPI_MAPPINGS
} from '../../utils/kpiUtils';

const KPIModelTest: React.FC = () => {
  const [testResults, setTestResults] = useState<string[]>([]);

  // Sample test data in new format with optional metadata
  const sampleInputData: FinancialRecordInput[] = [
    { 
      brand: 'Toyota', 
      dealer: 'ABC Motors', 
      year: 2023, 
      month: 1, 
      kpis: [
        { code: 'A0100.1', name: '', value: 150, category: undefined, subcategory: undefined, unit: undefined }
      ]
    },
    { 
      brand: 'Toyota', 
      dealer: 'ABC Motors', 
      year: 2023, 
      month: 1, 
      kpis: [
        { code: 'A0100.2', name: '', value: 2500000, category: undefined, subcategory: undefined, unit: undefined }
      ]
    },
    { 
      brand: 'Toyota', 
      dealer: 'ABC Motors', 
      year: 2023, 
      month: 1, 
      kpis: [
        { code: 'A0100.4', name: '', value: 125000, category: undefined, subcategory: undefined, unit: undefined }
      ]
    },
    { 
      brand: 'Toyota', 
      dealer: 'ABC Motors', 
      year: 2023, 
      month: 2, 
      kpis: [
        { code: 'A0100.1', name: '', value: 175, category: undefined, subcategory: undefined, unit: undefined }
      ]
    },
    { 
      brand: 'Honda', 
      dealer: 'XYZ Dealers', 
      year: 2023, 
      month: 1, 
      kpis: [
        { code: 'A0100.1', name: '', value: 120, category: undefined, subcategory: undefined, unit: undefined }
      ]
    },
    { 
      brand: 'Honda', 
      dealer: 'XYZ Dealers', 
      year: 2023, 
      month: 1, 
      kpis: [
        { 
          code: 'CUSTOM001', 
          name: 'Custom Performance Metric', 
          value: 50000, 
          category: 'Performance', 
          subcategory: 'Efficiency', 
          unit: 'currency' 
        }
      ]
    }
  ];

  const runTests = () => {
    const results: string[] = [];
    
    try {
      // Test 1: KPI Enrichment
      results.push('=== Test 1: KPI Enrichment ===');
      const enrichedKPI = enrichKPI('A0100.1', 150);
      results.push(`✓ Standard KPI enriched: ${enrichedKPI.name} (${enrichedKPI.category})`);
      
      const unknownKPI = enrichKPI('UNKNOWN001', 100);
      results.push(`✓ Unknown KPI enriched: ${unknownKPI.name} (${unknownKPI.category})`);

      // Test 2: Value Parsing
      results.push('\n=== Test 2: Value Parsing ===');
      const parsedCurrency = parseKPIValue('$1,234.56');
      results.push(`✓ Currency parsed: $1,234.56 → ${parsedCurrency}`);
      
      const parsedPercentage = parseKPIValue('15.5%');
      results.push(`✓ Percentage parsed: 15.5% → ${parsedPercentage}`);
      
      const parsedNegative = parseKPIValue('(1,000)');
      results.push(`✓ Negative parsed: (1,000) → ${parsedNegative}`);

      // Test 3: Input Validation
      results.push('\n=== Test 3: Input Validation ===');
      const validation = validateFinancialInputs(sampleInputData);
      results.push(`✓ Sample data validation: ${validation.isValid ? 'PASSED' : 'FAILED'}`);
      if (!validation.isValid) {
        validation.errors.forEach(error => results.push(`  - ${error}`));
      }

      // Test 4: Data Grouping
      results.push('\n=== Test 4: Data Grouping ===');
      const groupedData = groupFinancialInputs(sampleInputData);
      results.push(`✓ Grouped into ${groupedData.size} unique records`);
      groupedData.forEach((inputs, key) => {
        results.push(`  - ${key}: ${inputs.length} KPIs`);
      });

      // Test 5: Record Conversion
      results.push('\n=== Test 5: Record Conversion ===');
      const records = convertInputsToRecords(groupedData);
      results.push(`✓ Converted to ${records.length} financial records`);
      
      records.forEach((record, index) => {
        results.push(`  Record ${index + 1}: ${record.brand} - ${record.dealer} (${record.year}/${record.month}) - ${record.kpis.length} KPIs`);
      });

      // Test 6: KPI Statistics
      results.push('\n=== Test 6: KPI Statistics ===');
      const stats = getKPIStatistics(records);
      results.push(`✓ Total KPIs: ${stats.totalKPIs}`);
      results.push(`✓ Unique KPI codes: ${stats.uniqueKPICodes}`);
      results.push(`✓ Avg KPIs per record: ${stats.avgKPIsPerRecord}`);
      results.push(`✓ Has duplicates: ${stats.hasDuplicates}`);
      results.push('✓ Category breakdown:');
      stats.categoryBreakdown.forEach(({ category, count }) => {
        results.push(`  - ${category}: ${count}`);
      });

      // Test 7: Standard Mappings
      results.push('\n=== Test 7: Standard KPI Mappings ===');
      results.push(`✓ Loaded ${Object.keys(STANDARD_KPI_MAPPINGS).length} standard mappings`);
      Object.entries(STANDARD_KPI_MAPPINGS).slice(0, 3).forEach(([code, mapping]) => {
        results.push(`  - ${code}: ${mapping.displayName} (${mapping.category})`);
      });

      results.push('\n🎉 All tests completed successfully!');

    } catch (error) {
      results.push(`❌ Test failed: ${error instanceof Error ? error.message : String(error)}`);
    }

    setTestResults(results);
  };

  const clearResults = () => {
    setTestResults([]);
  };

  return (
    <div style={{ padding: '20px', fontFamily: 'monospace' }}>
      <h2>KPI Data Model Test Component</h2>
      <p>This component tests the new KPI-based financial record structure and utilities.</p>
      
      <div style={{ marginBottom: '20px' }}>
        <button 
          onClick={runTests}
          style={{ 
            padding: '10px 20px', 
            marginRight: '10px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Run Tests
        </button>
        <button 
          onClick={clearResults}
          style={{ 
            padding: '10px 20px',
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Clear Results
        </button>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <h3>New Input Format Features:</h3>
        <div style={{ 
          backgroundColor: '#e8f5e8', 
          padding: '15px', 
          borderRadius: '4px',
          fontSize: '14px'
        }}>
          <h4>Enhanced Input Structure:</h4>
          <ul>
            <li><strong>brand, dealer, year, month</strong> (required): Record identifiers</li>
            <li><strong>kpis</strong> (required): Array of KPI objects with:</li>
            <ul>
              <li><strong>code</strong> (required): The KPI identifier code</li>
              <li><strong>name</strong> (optional): Display name for the KPI</li>
              <li><strong>value</strong> (required): Numeric value of the KPI</li>
              <li><strong>category</strong> (optional): KPI category (e.g., "Sales", "Finance")</li>
              <li><strong>subcategory</strong> (optional): KPI subcategory (e.g., "New Vehicles")</li>
              <li><strong>unit</strong> (optional): Units of measurement (e.g., "currency", "units", "percentage")</li>
            </ul>
            <li><strong>additionalData</strong> (optional): Array of additional column data objects with:</li>
            <ul>
              <li><strong>field</strong>: The name of the additional column</li>
              <li><strong>value</strong>: The value from the additional column</li>
            </ul>
          </ul>
          <p><strong>Benefits:</strong></p>
          <ul>
            <li>Flexible KPI definitions without predefined mappings</li>
            <li>User-provided metadata overrides standard mappings</li>
            <li>Support for custom KPIs with rich metadata</li>
            <li>Backward compatibility with standard automotive KPI codes</li>
            <li>Capture additional data columns from CSV files</li>
          </ul>
        </div>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <h3>Sample Input Data (New Format):</h3>
        <pre style={{ 
          backgroundColor: '#f8f9fa', 
          padding: '10px', 
          borderRadius: '4px',
          fontSize: '12px',
          overflow: 'auto'
        }}>
{`Brand,Dealer,Year,Month,KPI Code,Value,KPI Name,Category,Subcategory,Units,Town,County
Toyota,ABC Motors,2023,1,A0100.1,150,,,,,Leeds,West Yorkshire
Toyota,ABC Motors,2023,1,A0100.2,2500000,,,,,Leeds,West Yorkshire
Toyota,ABC Motors,2023,1,A0100.4,125000,,,,,Leeds,West Yorkshire
Toyota,ABC Motors,2023,2,A0100.1,175,,,,,Leeds,West Yorkshire
Honda,XYZ Dealers,2023,1,A0100.1,120,,,,,Manchester,Greater Manchester
Honda,XYZ Dealers,2023,1,CUSTOM001,50000,Custom Performance Metric,Performance,Efficiency,currency,Manchester,Greater Manchester`}
        </pre>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <h3>Expected Output Structure:</h3>
        <pre style={{ 
          backgroundColor: '#f8f9fa', 
          padding: '10px', 
          borderRadius: '4px',
          fontSize: '12px',
          overflow: 'auto'
        }}>
{`{
  "id": "Toyota-ABC Motors-2023-1",
  "brand": "Toyota",
  "dealer": "ABC Motors", 
  "year": 2023,
  "month": 1,
  "kpis": [
    {
      "code": "A0100.1",
      "name": "New Vehicle Units Sold",
      "value": 150,
      "category": "Sales",
      "subcategory": "New Vehicles",
      "unit": "units"
    },
    ...
  ],
  "additionalData": [
    {
      "field": "Town",
      "value": "Leeds"
    },
    {
      "field": "County",
      "value": "West Yorkshire"
    }
  ]
}`}
        </pre>
      </div>

      {testResults.length > 0 && (
        <div>
          <h3>Test Results:</h3>
          <pre style={{ 
            backgroundColor: '#f8f9fa', 
            padding: '15px', 
            borderRadius: '4px',
            fontSize: '12px',
            maxHeight: '500px',
            overflow: 'auto',
            whiteSpace: 'pre-wrap'
          }}>
            {testResults.join('\n')}
          </pre>
        </div>
      )}
    </div>
  );
};

export default KPIModelTest; 